import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button, DateInput, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { FiltersDrawerRadioGroupItem } from '@common/components/FilterGroups/FiltersDrawerRadioGroup';
import type { DateValue } from '@internationalized/date';

import { AccordionSection } from '@common/components/AccordionSection';
import { FiltersDrawerRadioGroup } from '@common/components/FilterGroups/FiltersDrawerRadioGroup';
import {
	renderToggleButtonGroups,
	type ToggleButtonOption,
} from '@common/components/FilterGroups/renderToggleButtonGroups';
import { dateValueToTimestamp, timestampToDateValue } from '@common/lib/time';

import {
	fulfillmentDateCustomRangeAtom,
	fulfillmentDateFilterAtom,
	fulfillmentTypeFilterAtom,
	updateFulfillmentDateCustomRangeAtom,
	updateFulfillmentDateFilterAtom,
	updateFulfillmentTypeFilterAtom,
} from '../../atoms/ordersAtoms';
import { useFilterFacetCounts } from '../../hooks/useFilterFacetCounts';
import { DeliveryDateFilter } from '../../types/filters';
import { formatDeliveryDateFilterLabel } from '../../utils/filters';

export type FulfillmentType = 'delivery' | 'pickup';

/**
 * FiltersDrawerFulfillmentSection component that provides fulfillment-based filtering options
 * wrapped in an accordion
 */
export function FiltersDrawerFulfillmentSection(): React.JSX.Element {
	const fulfillmentType = useAtomValue(fulfillmentTypeFilterAtom);
	const fulfillmentDateFilter = useAtomValue(fulfillmentDateFilterAtom);
	const customRange = useAtomValue(fulfillmentDateCustomRangeAtom);

	const setFulfillmentType = useSetAtom(updateFulfillmentTypeFilterAtom);
	const setFulfillmentDateFilter = useSetAtom(updateFulfillmentDateFilterAtom);
	const setCustomRange = useSetAtom(updateFulfillmentDateCustomRangeAtom);

	const [localFrom, setLocalFrom] = useState<number | null>(customRange.from);
	const [localTo, setLocalTo] = useState<number | null>(customRange.to);

	// Sync local state when atom values change (e.g., external reset)
	useEffect(() => {
		setLocalFrom(customRange.from);
		setLocalTo(customRange.to);
	}, [customRange.from, customRange.to]);

	// Get dynamic facet counts from the search response
	const { deliveryCount, pickupCount } = useFilterFacetCounts();

	// Toggle button options for fulfillment type
	const fulfillmentTypeOptions: ToggleButtonOption<FulfillmentType>[] = useMemo(
		() => [
			{
				value: 'delivery',
				label: (
					<Group itemsSpacing='8' itemsAlignX='space-between' itemsAlignY='center' noWrap>
						<Text size='bodySmall'>Delivery</Text>
						<Text size='bodySmall' color='secondary'>
							{deliveryCount ?? 0}
						</Text>
					</Group>
				),
			},
			{
				value: 'pickup',
				label: (
					<Group itemsSpacing='8' itemsAlignX='space-between' itemsAlignY='center' noWrap>
						<Text size='bodySmall'>Pickup</Text>
						<Text size='bodySmall' color='secondary'>
							{pickupCount ?? 0}
						</Text>
					</Group>
				),
			},
		],
		[deliveryCount, pickupCount],
	);

	// Shared fulfillment date filter options (same for delivery and pickup)
	const fulfillmentDateItems: FiltersDrawerRadioGroupItem[] = [
		{
			value: DeliveryDateFilter.TODAY,
			label: formatDeliveryDateFilterLabel(DeliveryDateFilter.TODAY),
		},
		{
			value: DeliveryDateFilter.THIS_WEEK,
			label: formatDeliveryDateFilterLabel(DeliveryDateFilter.THIS_WEEK),
		},
		{
			value: DeliveryDateFilter.NEXT_2_WEEKS,
			label: formatDeliveryDateFilterLabel(DeliveryDateFilter.NEXT_2_WEEKS),
		},
		{
			value: DeliveryDateFilter.THIS_MONTH,
			label: formatDeliveryDateFilterLabel(DeliveryDateFilter.THIS_MONTH),
		},
		{
			value: DeliveryDateFilter.NEXT_MONTH_AND_BEYOND,
			label: formatDeliveryDateFilterLabel(DeliveryDateFilter.NEXT_MONTH_AND_BEYOND),
		},
		{
			value: DeliveryDateFilter.CUSTOM,
			label: formatDeliveryDateFilterLabel(DeliveryDateFilter.CUSTOM),
		},
	];

	// Handle fulfillment type toggle change
	const handleFulfillmentTypeToggle =
		(fulfillmentTypeValue: FulfillmentType) => (isSelected: boolean) => {
			if (isSelected) {
				setFulfillmentType(fulfillmentTypeValue);
			} else {
				// Deselecting clears both type and date filter
				setFulfillmentType(null);
				setFulfillmentDateFilter(null);
			}
		};

	// Handle fulfillment date filter change (single selection)
	const handleFulfillmentDateChange = useCallback(
		(value: string) => {
			// Only allow changes when a fulfillment type is selected
			if (fulfillmentType) {
				const newFilter = value as DeliveryDateFilter;
				if (newFilter !== DeliveryDateFilter.CUSTOM) {
					setLocalFrom(null);
					setLocalTo(null);
				}
				setFulfillmentDateFilter(newFilter);
			}
		},
		[fulfillmentType, setFulfillmentDateFilter],
	);

	const handleCustomFromChange = useCallback((value: DateValue | null) => {
		setLocalFrom(dateValueToTimestamp(value) ?? null);
	}, []);

	const handleCustomToChange = useCallback((value: DateValue | null) => {
		setLocalTo(dateValueToTimestamp(value) ?? null);
	}, []);

	const handleApplyCustomRange = useCallback(() => {
		setCustomRange({ from: localFrom, to: localTo });
	}, [localFrom, localTo, setCustomRange]);

	// Determine the label for the date radio group
	const dateLabel = fulfillmentType
		? fulfillmentType === 'delivery'
			? 'Delivery date'
			: 'Pickup date'
		: 'Date';

	const isCustomSelected = fulfillmentDateFilter === DeliveryDateFilter.CUSTOM;

	return (
		<AccordionSection title='Fulfillment'>
			<Stack itemsSpacing='24'>
				{renderToggleButtonGroups({
					options: fulfillmentTypeOptions,
					currentValue: fulfillmentType,
					onToggle: handleFulfillmentTypeToggle,
				})}
				<FiltersDrawerRadioGroup
					key={`fulfillment-date-${fulfillmentDateFilter ?? 'none'}-${
						fulfillmentType ?? 'none'
					}`}
					label={dateLabel}
					items={fulfillmentDateItems}
					value={fulfillmentDateFilter ?? undefined}
					onChange={handleFulfillmentDateChange}
					isDisabled={!fulfillmentType}
				/>
				{isCustomSelected && fulfillmentType && (
					<Stack itemsSpacing='8'>
						<DateInput
							label='From'
							value={timestampToDateValue(localFrom)}
							onChange={handleCustomFromChange}
							size='medium'
						/>
						<DateInput
							label='To'
							value={timestampToDateValue(localTo)}
							onChange={handleCustomToChange}
							size='medium'
						/>
						<Button variant='secondary' size='small' onPress={handleApplyCustomRange}>
							Apply
						</Button>
					</Stack>
				)}
			</Stack>
		</AccordionSection>
	);
}
